<template>
  <div data-cy="document-panel" class="document-panel n-pt-05">
    <div v-if="documentStore.documentModels.length <= 0" class="n-txt--center no-documents-text">
      {{ $t('no-documents-loaded') }}
    </div>
    <a-collapse
      v-model:activeKey="activeKey"
      :bordered="false"
      :accordion="false"
      class="document-accordion"
      expand-icon-position="left"
      default-active-key="0"
    >
      <template #expandIcon="{ isActive }">
        <caret-right-outlined :rotate="isActive ? 90 : 0" />
      </template>
      <a-collapse-panel
        v-for="(doc, docIndex) of documentStore.documentModels"
        :key="getKey(docIndex)"
      >
        <document-reader
          class="document-item"
          data-cy="document-item"
          :model="doc"
          :preview-type="documentStore.selectedDocumentPanelView"
          :enabled-tags="enabledTags"
        />
        <template #extra>
          <div class="columns is-flex is-justify-content-space-between">
            <div
              data-cy="document-header"
              class="n-flex-grow document-accordion--header n-mr-2 n-txt--left pt-1"
            >
              <div class="column header-tags-column">
                <n-tag
                  data-cy="document-id-tag"
                  icon="key"
                  label="Document ID"
                  :value="getSourceDocumentId(doc)"
                />
                <n-tag
                  data-cy="document-date-tag"
                  icon="calendar"
                  label="Date"
                  :value="getFriendlyDate(doc.FileDate || doc.CreationDateTime)"
                />
                <n-tag data-cy="document-type-tag" label="Type" :value="doc.LetterType" />
                <n-tag data-cy="document-title-tag" label="Doc Title" :value="doc.DocumentTitle" />
                <n-tag
                  data-cy="document-category-tag"
                  label="Doc Category"
                  :value="doc.DocumentCategory"
                />
                <n-tag
                  data-cy="document-rova-version-tag"
                  label="Rova"
                  :value="getRovaVersion(doc)"
                />
                <template v-if="doc.ClassificationStatus === 'classified'">
                  <div
                    v-for="(match, index) of getClassificationGroups(doc.classifications)"
                    data-cy="document-header-classification"
                    :key="index"
                    :class="{ 'mr-1': match.result !== 'negative' }"
                  >
                    <template
                      v-if="
                        match.result !== 'negative' && !match.hideInDocumentReader && match.sentence
                      "
                    >
                      <n-icon
                        v-for="(classifier, cIndex) of match.classifiers"
                        :key="`classifier-${cIndex}`"
                        class="mr-1"
                        :icon="['fal', documentNLPMixin.getClassificationStatusIcon(classifier)]"
                        :class="[
                          `has-background-${documentNLPMixin.getClassificationStatusColor(classifier)} 
                          has-text-${documentNLPMixin.getClassificationStatusColor(classifier) === 'grey-dark' ? 'white' : 'black'} document-header-icon`,
                        ]"
                      />
                      <span
                        v-if="match.sentence"
                        data-cy="document-header-sentence"
                        style="font-style: italic"
                        @click.stop="scrollToSentence(doc)"
                      >
                        {{ match.sentence }}
                      </span>
                    </template>
                  </div>
                </template>
                <div v-else data-cy="document-header-classification-not-classified">
                  <n-icon
                    :icon="['fal', documentNLPMixin.getClassificationLabelIcon(doc)]"
                    :css-class="documentNLPMixin.getClassificationLabelIconClass(doc)"
                  />
                  {{ doc.ClassificationStatusLabel }}
                </div>
              </div>
            </div>
            <div class="column is-3 header-buttons-column" style="text-align: center">
              <div @click="handleClick">
                <a-radio-group
                  v-if="view !== 'probe'"
                  v-model:value="documentStore.selectedDocumentPanelView"
                  button-style="solid"
                  size="small"
                  :default-value="getDefaultView(doc)"
                >
                  <a-radio-button v-if="showOriginalView(doc)" :value="'text'">
                    {{ 'Text' }}
                  </a-radio-button>
                  <a-radio-button :value="'pdf'" class="mt-1">
                    {{ 'Original' }}
                  </a-radio-button>
                </a-radio-group>
                <div v-if="view === 'probe'">
                  <n-tooltip :text="$t('Classifier Source')" placement="bottom">
                    <a-radio-group
                      v-if="view === 'probe'"
                      data-cy="probe-document-switch"
                      v-model:value="uiStore.state.probeHighlighting"
                      button-style="solid"
                      @change="onSwitchChange"
                    >
                      <a-radio-button class="n-m-0 doc-btn doc-probe-btn" value="probe"
                        >Probe</a-radio-button
                      >
                      <a-radio-button class="n-m-0 doc-btn" value="rova">Rova</a-radio-button>
                      <a-radio-button class="n-m-0 doc-btn doc-regex-btn" value="regex"
                        >Regex</a-radio-button
                      >
                    </a-radio-group>
                  </n-tooltip>
                </div>
              </div>
              <a-button
                v-if="ability.can('view', 'context:linkageValidation')"
                class="modal-button linkage-button"
                type="primary"
                @click="openLinkageModal(doc)"
                data-cy="validate-linkage-button"
              >
                <span class="linkage-button-label">Validate Linkage</span>
              </a-button>
            </div>
          </div>
        </template>
      </a-collapse-panel>
    </a-collapse>
    <a-modal v-model:open="showLinkageModal" title="Validate Document Linkage" :footer="null">
      <div class="linkage-modal-content">
        <div class="linkage-info">
          <p data-cy="linkage-target">
            <strong>Linked To:</strong> {{ detectedLinkageTarget }}
            <span v-if="detectedLinkageTargetId">({{ detectedLinkageTargetId }})</span>
          </p>
          <h2>Linkage Method:</h2>
          <p data-cy="linkage-description">{{ linkageDescription }}</p>
        </div>
        <a-row class="mb-2">
          <a-col :span="24"><div class="subtitle mb-1">Validation Linkage History</div></a-col>
          <a-col :span="24">
            <a-table
              data-cy="validation-history-table"
              :data-source="validationHistory"
              :columns="validationColumns"
              :pagination="false"
              :scroll="{ x: 500, y: 500 }"
            >
              <template #expandedRowRender="{ record }">
                <p
                  data-cy="classification-popover-expanded-row"
                  style="margin: 0"
                  :text="record.ValidationComment"
                />
              </template>
            </a-table>
          </a-col>
        </a-row>
        <a-form
          v-if="showRejectForm"
          ref="rejectForm"
          data-cy="linkage-reject-form"
          :model="rejectFormState"
          autocomplete="off"
        >
          <a-form-item
            name="ValidationRejectionReason"
            :rules="[{ required: true, message: 'Please select a reason!' }]"
          >
            <div class="is-size-6 mb-1">{{ $t('Rejection Reason') }}</div>
            <input-autocomplete-dropdown
              v-model:value="rejectFormState.ValidationRejectionReason"
              data-cy="linkage-reject-reason-dropdown"
              :data-source="linkageRejectionReasons"
              :get-popup-container="(trigger) => trigger.parentNode"
            />
            <a-row class="mt-4" v-if="rejectSelectedItem.description">
              <div class="is-size-6 mb-1">{{ $t('Rejection Description') }}</div>
              <a-row class="has-text-grey">{{ rejectSelectedItem.description }}</a-row>
            </a-row>
          </a-form-item>
          <a-form-item
            name="ValidationComment"
            :rules="[
              {
                required: rejectFormState.ValidationRejectionReason === 'Other',
                message: 'Please input a comment!',
              },
            ]"
          >
            <div class="is-size-6 mb-1">Comment</div>
            <a-textarea
              v-model:value="rejectFormState.ValidationComment"
              data-cy="linkage-reject-comment"
              :rows="4"
            />
          </a-form-item>
        </a-form>
        <div class="linkage-actions">
          <template v-if="!showRejectForm">
            <a-button
              type="primary"
              data-cy="linkage-approve-button"
              @click="handleLinkageAction('approve')"
            >
              Approve
            </a-button>
            <a-button danger data-cy="linkage-reject-button" @click="showRejectForm = true">
              Reject
            </a-button>
          </template>
          <template v-else>
            <a-button
              danger
              type="primary"
              data-cy="linkage-confirm-reject-button"
              @click="submitRejection"
            >
              Confirm Reject
            </a-button>
            <a-button @click="showRejectForm = false"> Back </a-button>
          </template>
          <a-button @click="showLinkageModal = false"> Close </a-button>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script setup>
import { find } from 'lodash'
import DocumentReader from '@/components/document-nlp/document-reader/document-reader.vue'
import { getFriendlyDate } from '@/mixins/n-time'
import NIcon from '@/components/ui/n-icon/n-icon.vue'
import NTag from '@/components/ui/n-tag/n-tag.vue'
import useDocumentStore from '@/store/modules/document'
import useUiStore from '@/store/modules/ui'
import { ref, reactive, computed, watch, onBeforeUnmount } from 'vue'
import useDocumentNLPMixin from '@/mixins/documentNLPMixin'
import { CaretRightOutlined } from '@ant-design/icons-vue'
import { notification } from 'ant-design-vue'
import NTooltip from '@/components/ui/n-tooltips/n-tooltip.vue'
import InputAutocompleteDropdown from '@/components/inputs/input-autocomplete-dropdown.vue'
import useValidationStore from '@/store/modules/validation'
import useConfigStore from '@/store/modules/config'
import useAbility from '@/services/ability.js'

const documentNLPMixin = useDocumentNLPMixin()
const documentStore = useDocumentStore()
const uiStore = useUiStore()
const configStore = useConfigStore()
const ability = useAbility()
const validationHistory = ref([])
const validationColumns = [
  {
    title: 'Date',
    dataIndex: 'ValidatedOn',
    key: 'ValidatedOn',
  },
  {
    title: 'User',
    dataIndex: 'ValidatedBy',
    key: 'ValidatedBy',
  },
  {
    title: 'Type',
    dataIndex: 'ValidationType',
    key: 'ValidationType',
  },
  {
    title: 'Rejection Reason',
    dataIndex: 'ValidationRejectionReason',
    key: 'ValidationRejectionReason',
  },
]
const activeKey = ref(['0'])
const enabledTags = ref(['Date', 'Specialty'])
const validationStore = useValidationStore()
const showLinkageModal = ref(false)
const detectedLinkageTarget = ref(null)
const detectedLinkageTargetId = ref(null)
const linkageDescription = ref(null)
const selectedDoc = ref(null)
const showRejectForm = ref(false)
const rejectForm = ref(null)
const rejectFormState = reactive({})

const linkageRejectionReasons = computed(
  () => configStore.getConfigByKey('web-config.dropdowns.linkageRejectionReasons') || [],
)

const rejectSelectedItem = computed(() => {
  return (
    linkageRejectionReasons.value[0]?.options.find(
      (el) => el.value === rejectFormState.ValidationRejectionReason,
    ) || {}
  )
})

defineProps({
  view: {
    type: String,
    required: false,
    default: '',
  },
})

const getSourceDocumentId = (doc) => {
  if (doc.RovaModel?.knownFacts?.SourceDocumentID) {
    return doc.RovaModel?.knownFacts?.SourceDocumentID
  }
  return doc.knownFacts?.SourceDocumentID
}

const scrollToSentence = (doc) => {
  const positiveClassifiers = doc.classifications.filter((c) => c.result !== 'negative')
  document
    .getElementById(`sentence-icon-classification-id-${positiveClassifiers[0]._id}`)
    .scrollIntoView({
      behavior: 'smooth',
    })
}

const showOriginalView = (doc) => {
  return !(doc.error === '404' || doc.TextContent === null)
}

const getDefaultView = (doc) => {
  if (doc.error === '404' || doc.TextContent === null) {
    return 'iframe'
  } else if (doc.TextContent && doc.DefaultViewer === 'original') {
    return 'iframe'
  }

  return documentStore.selectedDocumentPanelView
}

const getClassificationGroups = (classifications) => {
  const groups = []

  for (const classification of classifications) {
    for (const match of classification.matches) {
      const matchedGroup = find(groups, { sentence: match.sentence })

      // If no match exists, create the group
      if (!matchedGroup) {
        groups.push({
          sentence: match.sentence,
          result: classification.result,
          classifiers: [classification.classifier],
          hideInDocumentReader: classification.hideInDocumentReader,
        })
      } else {
        // If a match exists, handle the additional classifier
        matchedGroup.classifiers.push(classification.classifier)
      }
    }
  }
  return groups
}

const getKey = (index) => {
  // See: #922 this starts index at 0 for a single doc and at 1 for multiple docs
  // as the activeKey is initialised to ['0'] this results in:
  // (a) Document expanded if a single doc is loaded into Docs panel
  // (b) Documents collapsed if multiple docs loaded.
  // #58366: activeKey is now set to every key on load, so all docs default to expanded
  return documentStore.documentModels.length === 1 ? index.toString() : (index + 1).toString()
}

const handleClick = (event) => {
  // If you don't want click extra trigger collapse, you can prevent this:
  event.stopPropagation()
}

const getRovaVersion = (doc) => {
  if (!doc.RovaVersion) {
    return null
  }
  return `v${doc.RovaVersion}`
}

const onSwitchChange = (event) => {
  documentStore.documentModels[0]?.classifications?.forEach((c) => {
    c.hideInDocumentReader = c.source === event.target.value ? false : true
  })
}

const openLinkageModal = async (doc) => {
  selectedDoc.value = doc
  resetRejectForm()
  const { target, targetId } = detectLinkageTarget(doc)
  detectedLinkageTarget.value = target
  detectedLinkageTargetId.value = targetId
  linkageDescription.value =
    selectedDoc.value.LinkageMethodDescription || 'No description available for this document.'

  // A target without an ID can't be tied to anything, so treat it the same as no target
  if (!detectedLinkageTarget.value || !detectedLinkageTargetId.value) {
    notification.warning({
      message: 'No Linkage Target Detected',
      description: 'This document does not have the required linkage metadata.',
    })
    return
  }

  try {
    // Fetch linkage history for this document
    await validationStore.getLinkageValidationHistory([doc.FileId])
    validationHistory.value = validationStore.filterLinkageHistory()
  } catch (error) {
    console.error('Error fetching linkage history:', error)
    notification.error({
      message: 'Error Loading History',
      description: 'Unable to load linkage validation history.',
    })
  }

  showLinkageModal.value = true
}

const detectLinkageTarget = (doc) => {
  // Use the same LinkedTo/LinkedToID as the document list so both views agree (#58366)
  const listDoc = documentStore.documents.find((d) => d.FileId === doc.FileId)

  if (listDoc) {
    // Only fall back to 'Pathway' when the document actually has a pathway
    const target = listDoc.LinkedTo || (listDoc.PTLUniqueID ? 'Pathway' : null)
    const isPathway = getLinkageTargetIdField(target) === 'PathwayID'
    const targetId = listDoc.LinkedToID || (isPathway ? listDoc.PTLUniqueID : null) || null
    return { target, targetId }
  }

  if (doc.PTLUniqueID) {
    return { target: 'Pathway', targetId: doc.PTLUniqueID }
  }

  return { target: null, targetId: null }
}

const getLinkageTargetIdField = (target) => {
  if (target === 'Activity') return 'ActivityID'
  if (target === 'Patient') return 'LocalPatientID'
  // 'Pathway', 'RTT Pathways'
  return 'PathwayID'
}

const resetRejectForm = () => {
  showRejectForm.value = false
  Object.keys(rejectFormState).forEach((key) => delete rejectFormState[key])
}

const submitRejection = async () => {
  try {
    await rejectForm.value.validate()
  } catch {
    return
  }

  await handleLinkageAction('reject', { ...rejectFormState })
}

const handleLinkageAction = async (action, rejection = {}) => {
  if (!detectedLinkageTarget.value || !detectedLinkageTargetId.value || !selectedDoc.value) return

  try {
    const knownFacts = selectedDoc.value.RovaModel?.knownFacts || selectedDoc.value.knownFacts || {}
    const payload = {
      FileId: selectedDoc.value.FileId,
      FileNumber: selectedDoc.value.FileNumber,
      RovaVersion: selectedDoc.value.RovaVersion,
      ValidationTarget: 'Linkage',
      LinkageTarget: detectedLinkageTarget.value,
      LinkageMethodDescription: selectedDoc.value.LinkageMethodDescription || null,
      ...rejection,
    }

    if (knownFacts.ActivityID) {
      payload.ActivityID = knownFacts.ActivityID
    }
    if (knownFacts.LocalPatientID) {
      payload.LocalPatientID = knownFacts.LocalPatientID
    }
    if (selectedDoc.value.PTLUniqueID) {
      payload.PathwayID = selectedDoc.value.PTLUniqueID
    }

    // The validated target's ID must be the one shown in the modal (from the document list)
    payload[getLinkageTargetIdField(detectedLinkageTarget.value)] = detectedLinkageTargetId.value

    await validationStore.submitLinkageValidation(action, payload)

    // Refresh linkage history after successful validation
    await validationStore.getLinkageValidationHistory([selectedDoc.value.FileId])
    validationHistory.value = validationStore.filterLinkageHistory()

    notification.success({
      message: 'Linkage Validation Saved',
      description: `Linkage has been ${action}d successfully.`,
    })
    showLinkageModal.value = false
  } catch (error) {
    notification.error({
      message: 'Validation Error',
      description: error.message || 'An error occurred while processing the linkage validation.',
    })
    console.error('Error handling linkage validation:', error)
  }
}

watch(
  () => documentStore.documentModels,
  (doc) => {
    if (doc) {
      onSwitchChange({ target: { value: uiStore.state.probeHighlighting } })
    }
  },
)

// Documents default to expanded (#58366). Kept separate from the watch above: running
// onSwitchChange on mount would hide classifications that have no matching `source`
watch(
  () => documentStore.documentModels,
  (doc) => {
    if (doc) {
      activeKey.value = doc.map((_, index) => getKey(index))
    }
  },
  { immediate: true },
)

// Lock page scrolling while the linkage modal is open, as the classification validation modal does
watch(
  () => showLinkageModal.value,
  (value) => {
    documentNLPMixin.setMainScrolling(value)
  },
)

onBeforeUnmount(() => {
  uiStore.state.probeHighlighting = 'rova'
})
</script>

<style lang="less">
.document-accordion {
  background: #fdfdfd;
  border-radius: 4px;
  margin-bottom: 24px;
  border: 0;
  overflow: hidden !important;

  & .ant-collapse-extra {
    display: block !important;
    flex: 12 !important;
  }
  & .ant-collapse-content-box {
    padding-top: 16px !important;
  }
  & .ant-collapse-content {
    color: black;
  }
  & .ant-collapse-header {
    width: 100% !important;
    background: #f7f7f7 !important;
  }
}
.document-tag-selector {
  background: rgba(241, 241, 241, 0.9);
  padding: 0.35rem 0.75rem 0.35rem;
  margin-bottom: 0.5rem;
  // -webkit-user-select: none;
  // -moz-user-select: none;
  // -ms-user-select: none;
  // user-select: none;
  display: inline-block;
  white-space: nowrap;
  cursor: pointer;
  border-radius: 5px;
}
.document-item {
  padding-bottom: 24px;
  margin-bottom: 24px;
  border-bottom: 1px dashed black;
  &:last-child {
    border-bottom: none;
  }
}
.header-buttons-column {
  padding: 15px 10px 0px 30px !important;
}
.header-tags-column {
  padding: 0px 12px 12px 12px !important;
  & .ant-tag {
    margin-bottom: 5px;
  }
}
.document-accordion {
  overflow-y: scroll;
}
.document-panel {
  overflow-y: scroll;
  height: 70vh;
}
.ant-collapse-expand-icon {
  align-self: center !important;
  padding-right: 10px !important;
}
.document-panel .ant-collapse-header-text {
  max-width: 0px !important;
}
.document-header-icon {
  border-radius: 3px;
  & .fa-icon {
    padding: 2px;
  }
}
.linkage-modal-content {
  padding: 16px 0;

  .linkage-info {
    margin-bottom: 24px;
    padding: 12px;
    border-radius: 4px;

    p {
      margin: 0;
      font-size: 14px;
    }
  }

  .linkage-actions {
    display: flex;
    flex-direction: row;
    gap: 8px;
    justify-content: flex-end;

    :deep(.ant-btn) {
      min-width: 80px;
    }
  }
}
.linkage-history {
  .linkage-history-title {
    font-weight: 600;
  }
}
.modal-button {
  margin-top: 10px;
  margin-right: 10px;
}
@media only screen and (max-width: 1280px) {
  .modal-button {
    margin-top: 10px;
    width: 100%;
    font-size: 10px;
    padding: 0px;
  }
}
// Let the linkage label wrap instead of being clipped in the narrow header column
.linkage-button {
  max-width: 100%;
  height: auto;
  min-height: 32px;
  white-space: normal;
  line-height: 1.2;
  padding: 4px 8px;
}
@media only screen and (max-width: 1280px) {
  .linkage-button {
    padding: 4px 2px;
    font-size: 11px;
  }
}
</style>
<style scoped>
.mr-1 {
  padding: 2px !important;
}
.highlighting-switch {
  margin-bottom: 10px;
}
.doc-btn {
  width: 70px;
  border-radius: 0px;
  display: grid;
  place-items: center;
  border-inline-start-width: 1px;
}
.doc-probe-btn {
  border-bottom: 0px;
}
.doc-regex-btn {
  border-top: 0px;
}
</style>
<style>
.document-header-icon .fa-ban {
  font-size: 0.83em;
}
</style>
